import React, { useEffect, useRef, useState } from 'react';
import { Drawer, Form, message } from 'antd';
import { useStore } from '../../store/store';
import { getPrimaryDark, PRIMARY_DARK } from '../../constants/constants';
import { formLabelWidth, leftRailWidth, leftSidebarContentWidth } from '../../constants/gridConfig';
import { hexToRgba } from '../../utils/theme';
import { claimBox, releaseBox } from '../../collab/useEditorCollab';
import { CONFIGURATOR_LOCK_ID } from '../../collab/lockIds';
import { anyRemoteLockHolder } from '../../collab/structureLocks';
import DuotoneLightIcon from '../common/DuotoneLightIcon';
import ConfigPagePreview from './ConfigPagePreview';
import { ConfigPreviewProvider } from '../../contexts/ConfigPreviewContext';
import { ConfigCompactProvider } from '../../contexts/ConfigCompactContext';
import { TabsColors } from '../RightSide/configurationModal/colorTab';
import { TabsMarges } from '../RightSide/configurationModal/marginTab';
import { TabsFonts } from '../RightSide/configurationModal/FontTab';
import { TabsTitles } from '../RightSide/configurationModal/titleTab';
import { TabsSummary } from '../RightSide/configurationModal/summaryTab';
import { TabsTables } from '../RightSide/configurationModal/tableTab';
import { TabsPages } from '../RightSide/configurationModal/pageTab';
import { TabsWatermark } from '../RightSide/configurationModal/watermarkTab';
import { TabsPageBackground } from '../RightSide/configurationModal/pageBackgroundTab';
import { TabsMasks } from '../RightSide/configurationModal/masksTab';
import { TabsVariable } from '../RightSide/configurationModal/variableTab';
import { DocumentTab } from '../RightSide/configurationModal/DocumentTab';
import { TabsPermissions } from '../RightSide/configurationModal/permissionsTab';

// Onglets de l'ex-modale de configuration (ConfigurationModal.jsx) réutilisés
// tels quels dans un Drawer gauche large : un clic sur une option ouvre le
// Drawer sur l'onglet correspondant. Labels/clés alignés sur la modale.
// `render` reçoit handleClose pour les onglets qui referment eux-mêmes (Masques).
const CONFIG_OPTIONS = [
  { key: 'Couleurs', label: 'Couleurs', iconName: 'palette', render: () => <TabsColors /> },
  { key: 'Marges', label: 'Marges', iconName: 'border-outer', render: () => <TabsMarges /> },
  { key: 'Police', label: 'Police', iconName: 'font', render: () => <TabsFonts /> },
  { key: 'Titres', label: 'Titres', iconName: 't', render: () => <TabsTitles /> },
  { key: 'Sommaire', label: 'Sommaire', iconName: 'list-tree', render: () => <TabsSummary /> },
  { key: 'Tableaux', label: 'Tableaux', iconName: 'table', render: () => <TabsTables /> },
  { key: 'N° page', label: 'N° page', iconName: 'hashtag', render: () => <TabsPages /> },
  { key: 'watermark', label: 'Filigrane', iconName: 'stamp', render: () => <TabsWatermark /> },
  { key: 'page-background', label: 'Décor de page', iconName: 'file-image', render: (close) => <TabsPageBackground closeConfiguration={close} /> },
  { key: 'masks', label: 'Masques', iconName: 'masks-theater', render: (close) => <TabsMasks closeConfiguration={close} /> },
  { key: 'variables', label: 'Styles des éléments', iconName: 'shapes', render: () => <TabsVariable /> },
  { key: 'document', label: 'Document', iconName: 'file-lines', render: () => <DocumentTab /> },
  { key: 'permissions', label: 'Autorisations', iconName: 'user-lock', render: () => <TabsPermissions /> },
];

// Grand écran : Drawer de WIDE_DRAWER_WIDTH à droite du panel de gauche, qui
// reste visible avec sa liste d'onglets. Dès que l'aperçu de page
// (ConfigPagePreview) n'a plus PREVIEW_RESERVED_WIDTH à sa droite (un 13" n'a
// même plus la place de l'afficher), mode compact : le Drawer recouvre le
// panel, en reprend les onglets dans une colonne étroite (NAV_WIDTH) et son
// formulaire se resserre entre COMPACT_MIN_WIDTH et COMPACT_MAX_WIDTH. En deçà
// du plancher, les formulaires seraient trop tassés : l'aperçu se masque.
const WIDE_DRAWER_WIDTH = 900;
const COMPACT_MAX_WIDTH = 720;
const COMPACT_MIN_WIDTH = 600;
const PREVIEW_RESERVED_WIDTH = 520;
const NAV_WIDTH = 76;

const optionButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '8px 10px',
  border: 'none',
  borderRadius: 8,
  background: 'transparent',
  color: PRIMARY_DARK,
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
};

// Onglet de la colonne compacte du Drawer : icône et libellé court dessous,
// comme les boutons du rail (cf. LeftSideRail).
const navButtonStyle = (isActive, accentColor) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  width: '100%',
  padding: '8px 4px',
  border: 'none',
  borderRadius: 8,
  background: isActive ? hexToRgba(accentColor, 0.1) : 'transparent',
  color: isActive ? accentColor : PRIMARY_DARK,
  fontFamily: 'inherit',
  fontSize: 10,
  fontWeight: isActive ? 700 : 500,
  lineHeight: 1.2,
  textAlign: 'center',
  cursor: 'pointer',
});

// Liste des onglets de configuration + Drawer gauche large qui les héberge.
// Réutilise le verrou collab exclusif du configurateur (CONFIGURATOR_LOCK_ID) :
// claim à l'ouverture (refusée si un co-éditeur édite), release à la fermeture.
const ConfigDrawer = () => {
  const [openKey, setOpenKey] = useState(null);
  // Réglages en cours d'essai partagés par l'onglet ouvert avec l'aperçu.
  const [previewValues, setPreviewValues] = useState({});
  const currentDocument = useStore(({ currentDocument }) => currentDocument);
  const accentColor = getPrimaryDark(currentDocument.type === 'template');
  const remoteEditLock = useStore((state) => anyRemoteLockHolder(state));
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleClose = () => {
    releaseBox(CONFIGURATOR_LOCK_ID);
    setOpenKey(null);
  };

  const handleOpen = (key) => {
    const holder = anyRemoteLockHolder(useStore.getState());
    if (holder) {
      message.warning(`Édition en cours par ${holder.name}`);
      return;
    }
    claimBox(CONFIGURATOR_LOCK_ID);
    setOpenKey(key);
  };

  // Claim perdu (lock_denied) ou édition d'un co-éditeur apparue pendant la
  // configuration : le configurateur cède (même logique que la Navbar).
  useEffect(() => {
    if (openKey && remoteEditLock) {
      message.warning(`Édition en cours par ${remoteEditLock.name}`);
      releaseBox(CONFIGURATOR_LOCK_ID);
      setOpenKey(null);
    }
  }, [openKey, remoteEditLock]);

  // Démontage alors que le drawer est ouvert (changement de document / d'onglet
  // du rail) : relâcher le verrou pour ne pas le laisser détenu. Ref pour ne
  // libérer qu'au démontage réel, pas à chaque changement d'onglet.
  const openKeyRef = useRef(openKey);
  openKeyRef.current = openKey;
  useEffect(() => () => {
    if (openKeyRef.current) releaseBox(CONFIGURATOR_LOCK_ID);
  }, []);

  const active = CONFIG_OPTIONS.find((opt) => opt.key === openKey);
  const leftPanelWidth = leftRailWidth + leftSidebarContentWidth;
  const compact = viewport.width < leftPanelWidth + WIDE_DRAWER_WIDTH + PREVIEW_RESERVED_WIDTH;
  // Compact : le drawer démarre au bord droit du rail et recouvre le panel de
  // gauche, dont la liste d'onglets ne sert plus une fois un onglet ouvert.
  const drawerLeft = compact ? leftRailWidth : leftPanelWidth;
  const drawerWidth = compact
    ? NAV_WIDTH + Math.max(
      COMPACT_MIN_WIDTH,
      Math.min(COMPACT_MAX_WIDTH, viewport.width - leftRailWidth - NAV_WIDTH - PREVIEW_RESERVED_WIDTH)
    )
    : WIDE_DRAWER_WIDTH;

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {CONFIG_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            type='button'
            className='config-option-btn'
            style={optionButtonStyle}
            onClick={() => handleOpen(opt.key)}
          >
            <DuotoneLightIcon name={opt.iconName} style={{ fontSize: 16, color: accentColor }} />
            <span style={{ flex: 1, textAlign: 'left' }}>{opt.label}</span>
            <DuotoneLightIcon name='chevron-right' weight='solid' style={{ fontSize: 11, color: 'rgba(11, 34, 57, 0.35)' }} />
          </button>
        ))}
      </div>
      <Drawer
        placement='left'
        width={drawerWidth}
        open={!!openKey}
        onClose={handleClose}
        title={active?.label}
        destroyOnClose
        // Le root antd démarre à drawerLeft et clippe le débordement : le
        // panneau glisse en translateX(-100%)->0, sa partie qui recouvrirait ce
        // qui est à sa gauche est ainsi découpée au bord au lieu de le balayer
        // pendant l'animation (même principe que le clipping du
        // TableConfigDrawer).
        rootStyle={{ left: drawerLeft, overflow: 'hidden' }}
        // Le corps ne défile plus en bloc : la colonne d'onglets (compact)
        // reste en place pendant que le formulaire défile.
        styles={{ body: { padding: 0, display: 'flex', overflow: 'hidden' } }}
      >
        {compact && (
          <nav
            aria-label='Onglets de configuration'
            style={{
              width: NAV_WIDTH,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: '12px 6px',
              overflowY: 'auto',
              borderRight: '1px solid rgba(11, 34, 57, 0.08)',
            }}
          >
            {CONFIG_OPTIONS.map((opt) => {
              const isActive = opt.key === openKey;
              return (
                <button
                  key={opt.key}
                  type='button'
                  className='config-nav-btn'
                  style={navButtonStyle(isActive, accentColor)}
                  aria-current={isActive ? 'page' : undefined}
                  // Le verrou est déjà tenu : changer d'onglet ne le redemande pas.
                  onClick={() => setOpenKey(opt.key)}
                >
                  <DuotoneLightIcon name={opt.iconName} style={{ fontSize: 16, color: accentColor }} />
                  {opt.label}
                </button>
              );
            })}
          </nav>
        )}
        {/* key : un nouvel onglet s'ouvre en haut, pas au scroll du précédent. */}
        <div key={openKey} style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '12px 24px 24px' }}>
          <ConfigPreviewProvider value={setPreviewValues}>
            <ConfigCompactProvider value={compact}>
              <Form
                labelCol={{ flex: `${formLabelWidth}px` }}
                wrapperCol={{ flex: 1 }}
                labelWrap
                colon={false}
                layout='horizontal'
                name='config_drawer_form'
              >
                {active?.render(handleClose)}
              </Form>
            </ConfigCompactProvider>
          </ConfigPreviewProvider>
        </div>
      </Drawer>
      <ConfigPagePreview tabKey={openKey} values={previewValues} left={drawerLeft + drawerWidth} viewport={viewport} />
    </>
  );
};

export default ConfigDrawer;
